// Version-history client (history spec §API). Thin REST wrappers over
// /api/v1; the interesting semantics (restore is a mergeable server-side
// edit; epoch rebase) live on the server and in the bridge respectively —
// this module only lists, previews, and requests restores.

import type { HttpClient } from "./ports";

/** One version row (history spec: newest first, merged across epochs). */
export interface VersionInfo {
  versionId: string;
  ts: number;
  sizeBytes: number;
  epoch: number;
}

/** A decoded version preview (server loads the snapshot → text). */
export interface VersionPreview {
  text: string;
  ts: number;
  /** True when the version references a GC'd attachment (history spec). */
  attachmentMissing?: boolean;
  /** Content hashes of the referenced attachments that are no longer
   * available (GC'd / never committed). Empty/absent when none. */
  attachmentsMissing?: string[];
}

/** What a restore reports back (history spec §API: the 202 body). Restore
 *  never fails on a missing attachment — the text is restored and the missing
 *  embeds are surfaced. */
export interface RestoreResult {
  attachmentMissing?: boolean;
  attachmentsMissing?: string[];
}

interface RawVersion {
  version_id: string;
  ts: number;
  size_bytes: number;
  epoch: number;
}

export class HistoryClient {
  constructor(
    private readonly http: HttpClient,
    private readonly vaultId: string,
  ) {}

  private base(fileId: string): string {
    return `/api/v1/vaults/${this.vaultId}/files/${fileId}/versions`;
  }

  /** List a file's versions, newest first (viewer+). */
  async list(fileId: string): Promise<VersionInfo[]> {
    const res = await this.http.request<RawVersion[]>({
      method: "GET",
      path: this.base(fileId),
    });
    if (res.status !== 200) throw new Error(`versions list failed: ${res.status}`);
    return res.body.map((v) => ({
      versionId: v.version_id,
      ts: v.ts,
      sizeBytes: v.size_bytes,
      epoch: v.epoch,
    }));
  }

  /** Preview a single version's decoded text (viewer+). */
  async preview(fileId: string, versionId: string): Promise<VersionPreview> {
    const res = await this.http.request<{
      text: string;
      ts: number;
      attachment_missing?: boolean;
      attachments_missing?: string[];
    }>({
      method: "GET",
      path: `${this.base(fileId)}/${versionId}`,
    });
    if (res.status !== 200) throw new Error(`version preview failed: ${res.status}`);
    const preview: VersionPreview = { text: res.body.text, ts: res.body.ts };
    const missing = res.body.attachments_missing ?? [];
    if (res.body.attachment_missing || missing.length > 0) {
      preview.attachmentMissing = true;
      preview.attachmentsMissing = missing;
    }
    return preview;
  }

  /**
   * Request a restore (editor+). The server diffs the old text against the
   * doc's current text and applies it as a normal mergeable edit — it fans
   * out to us over the sync channel like any other change (history spec
   * §Restore semantics). Returns once the server accepts (202).
   */
  async restore(fileId: string, versionId: string): Promise<RestoreResult> {
    const res = await this.http.request<{
      attachment_missing?: boolean;
      attachments_missing?: string[];
    }>({
      method: "POST",
      path: `/api/v1/vaults/${this.vaultId}/files/${fileId}/restore`,
      body: { version_id: versionId },
    });
    if (res.status !== 202) throw new Error(`restore failed: ${res.status}`);
    const missing = res.body?.attachments_missing ?? [];
    return res.body?.attachment_missing || missing.length > 0
      ? { attachmentMissing: true, attachmentsMissing: missing }
      : {};
  }
}
